import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import type { ProjectStats } from '../api/types';

/**
 * Stats Panel
 *
 * Project dashboard reports (doc 06 §11): word counts (total, per speaker,
 * per graph/subtree), node counts by type, branch factor, a longest-path
 * estimate, asset storage usage, and recently edited graphs. Renders
 * `GET /api/projects/{project}/stats` (backed by `StatsService`).
 *
 * Styled with plain Bootstrap/Tabler utility classes, matching
 * ImportExportDialog.tsx and ProjectsView.tsx's convention (no external
 * component library in this codebase).
 */

interface StatsPanelProps {
    projectUuid: string;
    onClose: () => void;
}

export function StatsPanel({ projectUuid, onClose }: StatsPanelProps) {
    const [stats, setStats] = useState<ProjectStats | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        apiClient
            .getStats(projectUuid)
            .then((data) => !cancelled && setStats(data))
            .catch((e) => !cancelled && setError((e as Error).message));

        return () => {
            cancelled = true;
        };
    }, [projectUuid]);

    return (
        <div
            className="modal d-block"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Project Stats</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        {!stats && !error && (
                            <div className="text-muted">
                                <span className="spinner-border spinner-border-sm me-2" />
                                Loading...
                            </div>
                        )}

                        {stats && (
                            <div className="row g-3">
                                <div className="col-6 col-md-3">
                                    <StatTile label="Total words" value={stats.words.total.toLocaleString()} />
                                </div>
                                <div className="col-6 col-md-3">
                                    <StatTile
                                        label="Branch factor"
                                        value={stats.branch_factor.toFixed(2)}
                                        hint="avg outgoing connections per node"
                                    />
                                </div>
                                <div className="col-6 col-md-3">
                                    <StatTile
                                        label="Longest path"
                                        value={String(stats.longest_path_estimate.length)}
                                        hint={stats.longest_path_estimate.cyclic ? 'graph has a cycle — estimate only' : 'edges, longest simple path'}
                                    />
                                </div>
                                <div className="col-6 col-md-3">
                                    <StatTile
                                        label="Asset storage"
                                        value={`${formatBytes(stats.assets.used_bytes)} / ${formatBytes(stats.assets.quota_bytes)}`}
                                        hint={`${stats.assets.percent_used}% used · ${stats.assets.count} assets`}
                                    />
                                </div>

                                <div className="col-12">
                                    <h6 className="mt-2">Nodes by type</h6>
                                    <table className="table table-sm">
                                        <tbody>
                                            {Object.entries(stats.node_counts_by_type)
                                                .sort(([, a], [, b]) => b - a)
                                                .map(([type, count]) => (
                                                    <tr key={type}>
                                                        <td>{type}</td>
                                                        <td className="text-end">{count}</td>
                                                    </tr>
                                                ))}
                                            {Object.keys(stats.node_counts_by_type).length === 0 && (
                                                <tr>
                                                    <td className="text-muted">No nodes yet</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="col-12 col-md-6">
                                    <h6>Words per speaker</h6>
                                    <table className="table table-sm">
                                        <tbody>
                                            {Object.entries(stats.words.per_speaker)
                                                .sort(([, a], [, b]) => b - a)
                                                .slice(0, 10)
                                                .map(([speaker, count]) => (
                                                    <tr key={speaker}>
                                                        <td>{speaker}</td>
                                                        <td className="text-end">{count}</td>
                                                    </tr>
                                                ))}
                                            {Object.keys(stats.words.per_speaker).length === 0 && (
                                                <tr>
                                                    <td className="text-muted">No dialogue text yet</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="col-12 col-md-6">
                                    <h6>Words per graph</h6>
                                    <table className="table table-sm">
                                        <tbody>
                                            {Object.entries(stats.words.per_graph)
                                                .sort(([, a], [, b]) => b - a)
                                                .slice(0, 10)
                                                .map(([graph, count]) => (
                                                    <tr key={graph}>
                                                        <td>{graph}</td>
                                                        <td className="text-end">{count}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="col-12">
                                    <h6>Recently edited graphs</h6>
                                    <table className="table table-sm">
                                        <tbody>
                                            {stats.recently_edited_graphs.map((g) => (
                                                <tr key={g.uuid}>
                                                    <td>{g.name || 'Untitled'}</td>
                                                    <td className="text-end text-muted small">
                                                        {new Date(g.updated_at).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div className="card p-2 h-100">
            <div className="text-muted small">{label}</div>
            <div className="h4 mb-0">{value}</div>
            {hint && <div className="text-muted small">{hint}</div>}
        </div>
    );
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}
